import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../supabaseClient';
import { normalizeLanguagePreferences } from './languagePreferences';

export function useLanguagePreferences(userId) {
  const owner = userId || null;
  const scope = useRef(null);
  // Reset synchronously on account changes so even the first render cannot
  // expose the previous account's preferences or an old in-flight result.
  if (scope.current?.owner !== owner) scope.current = { owner, task: null };
  const [snapshot, setSnapshot] = useState(null);
  const [reloadVersion, setReloadVersion] = useState(0);
  const current = snapshot?.scope === scope.current ? snapshot : null;
  const settings = current?.settings || normalizeLanguagePreferences(null);

  const load = useCallback(() => {
    const target = scope.current;
    if (target.owner !== owner) return Promise.reject(new Error('Your account changed. Please generate again.'));
    if (target.task) return target.task;
    target.task = (async () => {
      let settings = normalizeLanguagePreferences(null);
      let error = null;
      if (owner) {
        try {
          const response = await supabase.from('datasettings')
            .select('input_language,output_language,spelling_format').eq('id', owner).maybeSingle();
          if (response.error) throw response.error;
          settings = normalizeLanguagePreferences(response.data);
        } catch {
          error = 'Language settings could not be loaded. Reports will use the default language settings.';
        }
      }
      settings = normalizeLanguagePreferences({ ...settings, ...target.edits });
      target.settings = settings;
      target.error = error;
      target.loaded = true;
      if (scope.current === target && !target.disposed) setSnapshot({ scope: target, settings, error });
      return settings;
    })();
    return target.task;
  }, [owner]);

  useEffect(() => {
    const target = scope.current;
    target.disposed = false;
    load().then(() => {
      // React StrictMode can re-run this effect while the same task is pending.
      if (scope.current === target && !target.disposed && target.settings) {
        setSnapshot({ scope: target, settings: target.settings, error: target.error });
      }
    });
    return () => { target.disposed = true; };
  }, [load, reloadVersion]);

  const getCurrentSettings = useCallback(async () => {
    const target = scope.current;
    await load();
    if (scope.current !== target || target.owner !== owner || target.disposed) {
      throw new Error('Your account changed. Please generate again.');
    }
    return target.settings;
  }, [load, owner]);

  const setPreference = useCallback((name, value) => {
    const target = scope.current;
    if (target.owner !== owner) return;
    const previous = target.settings || normalizeLanguagePreferences(null);
    const nextValue = typeof value === 'function' ? value(previous[name]) : value;
    target.edits = { ...target.edits, [name]: nextValue };
    const settings = normalizeLanguagePreferences({ ...previous, [name]: nextValue });
    target.settings = settings;
    setSnapshot({ scope: target, settings, error: target.error });
  }, [owner]);

  const reload = useCallback(() => {
    scope.current.disposed = true;
    scope.current = { owner, task: null };
    setReloadVersion(version => version + 1);
  }, [owner]);

  return {
    settings,
    loading: !current || !scope.current.loaded,
    error: current?.error || null,
    setPreference,
    getCurrentSettings,
    reload,
  };
}
